import { DocumentPreview, DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { DocumentListItem } from '@/components/MilestonePackagePartials/Documents/DocumentListItem';
import { getPreviewUrl, getOriginalUrl } from '@/helpers/documentHelpers';
import { useGetDocumentByAccountAndDocumentIdQuery } from '@/services/documents';
import { Box, Icon, TextInput } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FlagSubject, AllDocumentTypes, documentTypeToRequirementFlagSettingsMap } from 'types';

import DateTimeDisplay from '../DateTime';

import { SimpleFlaggedSectionBox } from './SimpleFlaggedSectionBox';

interface AttachedDocumentItemProps {
  documentId: string;
  accountId: string;
  onDocumentLoaded: (document: any) => void;
  onClick: (document: any) => (fileIndex: number) => void;
  baseCategory?: string;
}

const AttachedDocumentItem = ({
  documentId,
  accountId,
  onDocumentLoaded,
  onClick,
  baseCategory,
}: AttachedDocumentItemProps) => {
  const { data: document } = useGetDocumentByAccountAndDocumentIdQuery({
    accountId,
    documentId,
  });

  useEffect(() => {
    if (document) {
      onDocumentLoaded(document);
    }
  }, [document, onDocumentLoaded]);

  if (!document) {
    return null;
  }

  return (
    <DocumentListItem
      item={document}
      showDocumentActions={true}
      showTypeLabel={false}
      allowArchive={false}
      baseCategory={baseCategory}
      radius="md"
      displayDate={false}
      onClick={onClick(document)}
      showFlagIndicator={false}
    />
  );
};

interface FlagHistoryItemProps {
  isOriginal?: boolean;
  flagSubject?: string;
  flag?: any;
  note?: string;
  date: string;
  flagDocumentId?: string;
  flaggedDocument?: any;
  allowArchive?: boolean;
  changedFromReviewPackageValue?: string;
  replacementDocument?: any;
  isSpecialFlaggedSection?: boolean;
  isLastItem?: boolean;
}

export const FlagHistoryItem = ({
  isOriginal,
  flagSubject,
  flag,
  note,
  date,
  flagDocumentId,
  flaggedDocument,
  allowArchive,
  changedFromReviewPackageValue,
  replacementDocument,
  isSpecialFlaggedSection,
  isLastItem,
}: FlagHistoryItemProps) => {
  const { id } = useParams<{
    id: string;
  }>();
  const noSubject = flagSubject === FlagSubject.none;
  const { data: uploadedFlagFilesRaw } = useGetDocumentByAccountAndDocumentIdQuery(
    flagDocumentId
      ? {
          accountId: id as string,
          documentId: flagDocumentId,
        }
      : skipToken,
  );
  const documentCreatedAtDate = DateTime.fromISO(date || '');
  const documentCreatedAtFormat =
    documentCreatedAtDate.year === DateTime.now().year ? 'LLL d, h:mm a' : 'LLL d, yyyy h:mm a';

  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false);
  const [targetDoc, setTargetDoc] = useState<DocumentPreview | null>(null);
  const [checklistAttachedDocuments, setChecklistAttachedDocuments] = useState<any[]>([]);

  const handleAttachedDocumentLoaded = useCallback((document: any) => {
    setChecklistAttachedDocuments((current) => {
      if (current.some((item) => item.id === document.id)) {
        return current;
      }

      return [...current, document];
    });
  }, []);

  const documents: DocumentPreview[] = useMemo(() => {
    const docs: DocumentPreview[] = [];

    if (uploadedFlagFilesRaw) {
      const uploadedDocs =
        uploadedFlagFilesRaw.files?.map((file: any, index: number) => {
          const viewUrls = file?.viewUrls;
          return {
            id: `${String(uploadedFlagFilesRaw.id)}-${index}`,
            documentId: uploadedFlagFilesRaw.id?.toString(),
            url: getPreviewUrl(viewUrls) ?? '',
            originalUrl: getOriginalUrl(viewUrls),
            contentType: file?.contentType,
            fileName: file?.originalName,
            createdAt: uploadedFlagFilesRaw.meta?.createdAt,
            type: uploadedFlagFilesRaw.type,
          };
        }) || [];
      docs.push(...uploadedDocs);
    }

    const documentToAdd = flaggedDocument || replacementDocument;
    if (documentToAdd) {
      const flaggedDocs =
        documentToAdd.files?.map((file: any, index: number) => {
          const viewUrls = file?.viewUrls;
          return {
            id: `${documentToAdd.id}-${index}`,
            documentId: documentToAdd.id,
            url: getPreviewUrl(viewUrls) ?? '',
            originalUrl: getOriginalUrl(viewUrls),
            contentType: file?.contentType,
            fileName: file?.originalName,
            createdAt: documentToAdd.meta?.createdAt,
            type: documentToAdd.type,
          };
        }) || [];
      docs.push(...flaggedDocs);
    }

    checklistAttachedDocuments.forEach((attachedDocument) => {
      const attachedDocs =
        attachedDocument.files?.map((file: any, index: number) => {
          const viewUrls = file?.viewUrls;

          return {
            id: `${attachedDocument.id}-${index}`,
            documentId: attachedDocument.id?.toString(),
            url: getPreviewUrl(viewUrls) ?? '',
            originalUrl: getOriginalUrl(viewUrls),
            contentType: file?.contentType,
            fileName: file?.originalName,
            createdAt: attachedDocument.meta?.createdAt,
            type: attachedDocument.type,
          };
        }) || [];

      docs.push(...attachedDocs);
    });

    return docs;
  }, [uploadedFlagFilesRaw, flaggedDocument, replacementDocument, checklistAttachedDocuments]);

  const togglePreviewModal = useCallback(
    (documentItem: any) => (fileIndex: number) => {
      if (typeof fileIndex !== 'number' || !documentItem) {
        return;
      }

      const targetFile = documentItem.files?.[fileIndex];
      if (!targetFile) {
        return;
      }

      const targetPreview = documents.find((doc) => doc.id === `${documentItem.id}-${fileIndex}`);
      if (targetPreview) {
        setTargetDoc(targetPreview);
        setIsFilePreviewModalOpen(true);
      }
    },
    [documents],
  );

  const targetDocFlagSettingType = targetDoc?.type
    ? documentTypeToRequirementFlagSettingsMap[targetDoc.type as AllDocumentTypes]
    : undefined;

  const uploadedFlagFiles = flagDocumentId ? uploadedFlagFilesRaw : null;

  return noSubject || note || changedFromReviewPackageValue || replacementDocument || isOriginal ? (
    <Box position="relative">
      {!isLastItem && <Box borderWidth="0 0 0 sm" height="100" position="absolute" zIndex="0" />}

      <Box display="flex" alignItems="flex-start" direction="row" margin="0 0 lg 0" gap="md">
        <Box position="relative" style={{ marginLeft: '-7px' }} background="secondary">
          <Icon name={'circle'} size="md" />
        </Box>

        <Box display="flex" width="100">
          {flaggedDocument || replacementDocument ? (
            <Box padding={'0 0 md 0'}>
              <DocumentListItem
                item={flaggedDocument || replacementDocument}
                showDocumentActions={true}
                showTypeLabel={false}
                allowArchive={allowArchive}
                baseCategory={flag?.category}
                radius="md"
                displayDate={true}
                onClick={togglePreviewModal(flaggedDocument || replacementDocument)}
              />
            </Box>
          ) : changedFromReviewPackageValue ? (
            <Box gap="xs" width={{ base: '2xl', tablet: '30' }} padding="0 0 md 0">
              <Box fontSize="md">Previous value:</Box>
              <Box>
                <TextInput
                  id="for-display-only"
                  name="for-display-only"
                  label=""
                  placeholder="No value available"
                  value={changedFromReviewPackageValue}
                  onChange={() => {}}
                  isDisabled={true}
                />
              </Box>
            </Box>
          ) : (
            <>
              {(noSubject || flagSubject === FlagSubject.epcFeedback) && (
                <Box padding="0 0 sm 0" direction="row" justifyContent="space-between">
                  <Box>
                    {flagSubject === FlagSubject.epcFeedback
                      ? 'Flag was actioned by Partner'
                      : 'Issue was acknowledged as addressed'}
                  </Box>
                  <DateTimeDisplay
                    displayLocalDateTime={true}
                    value={date || ''}
                    excludeTime={true}
                    dateFormat={documentCreatedAtFormat}
                    fontSize="sm"
                    hoverDateFormat="DD h:mm:ss a"
                  />
                </Box>
              )}
              {isSpecialFlaggedSection && <SimpleFlaggedSectionBox label={flag.dataKey} />}
            </>
          )}
          {note || isOriginal ? (
            <>
              <Box padding="0 0 sm 0" direction="row" justifyContent="space-between">
                <Box direction="row" gap="xs" alignItems="center">
                  <Icon size="xl" name="reply" />
                  <Box>{flagSubject === FlagSubject.epcFeedback ? 'Flag Response' : 'QC response'}</Box>
                </Box>
                <Box>
                  <DateTimeDisplay
                    displayLocalDateTime={true}
                    value={date || ''}
                    excludeTime={true}
                    dateFormat={documentCreatedAtFormat}
                    fontSize="sm"
                    hoverDateFormat="DD h:mm:ss a"
                  />
                </Box>
              </Box>
              <Box display="flex" background="default" radius="md" padding="sm" direction="column">
                {flagSubject !== FlagSubject.epcFeedback && (
                  <>
                    <Box fontWeight="medium" fontSize="md" margin="0 0 sm 0">
                      Notes:
                    </Box>
                    {note ? (
                      <Box fontSize="sm">{note}</Box>
                    ) : (
                      <Box fontSize="sm" color="body-secondary">
                        {`There are no ${isOriginal ? 'initial' : 'additional'}  notes attached to this review`}
                      </Box>
                    )}
                  </>
                )}

                <Box
                  fontWeight="medium"
                  fontSize="md"
                  margin={flagSubject === FlagSubject.epcFeedback ? '0 0 sm 0' : 'md 0 xs 0'}
                >
                  Documents:
                </Box>
                {uploadedFlagFiles || (isOriginal && flag?.attachedDocumentIds?.length > 0) ? (
                  <Box direction="column" gap="2xs">
                    {uploadedFlagFiles && (
                      <DocumentListItem
                        item={uploadedFlagFiles}
                        showDocumentActions={true}
                        showTypeLabel={false}
                        allowArchive={flagSubject !== FlagSubject.epcFeedback}
                        baseCategory={flag?.category}
                        radius="md"
                        displayDate={false}
                        onClick={togglePreviewModal(uploadedFlagFiles)}
                      />
                    )}

                    {isOriginal &&
                      flag?.attachedDocumentIds?.map((documentId: string) => (
                        <AttachedDocumentItem
                          key={documentId}
                          documentId={documentId}
                          accountId={id as string}
                          onDocumentLoaded={handleAttachedDocumentLoaded}
                          onClick={togglePreviewModal}
                          baseCategory={flag?.category}
                        />
                      ))}
                  </Box>
                ) : (
                  <Box fontSize="sm" color="body-secondary">
                    {`No ${isOriginal ? 'initial' : 'additional'} documents attached to this review`}
                  </Box>
                )}
              </Box>
            </>
          ) : null}
        </Box>
      </Box>

      {targetDoc && (
        <DocumentPreviewModal
          targetDoc={targetDoc}
          flagType={targetDocFlagSettingType}
          setTargetDoc={setTargetDoc}
          isFilePreviewModalOpen={isFilePreviewModalOpen}
          documentList={documents}
          closeFilePreviewModal={() => setIsFilePreviewModalOpen(false)}
          baseCategory={flag?.category}
        />
      )}
    </Box>
  ) : null;
};
